// SPDX-License-Identifier: BUSL-1.1

/**
 * Sessions tab for the agent-detail page.
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 */

import { useMutation } from '@tanstack/react-query';
import { DataTable } from '@/components/data-table';
import { LiveStreamPanel } from '@/components/live-stream-panel';
import { SessionMessageInput } from '@/components/session-message-input';
import { Button } from '@/components/ui/button';
import type { Session } from '@/types/api';
import { sessionColumns } from './session-columns';

export function SessionsTab({
  sessionData,
  streamSessionId,
  setStreamSessionId,
  terminate,
  displayName,
}: {
  sessionData: Session[];
  streamSessionId: string | null;
  setStreamSessionId: (id: string | null) => void;
  terminate: ReturnType<typeof useMutation<unknown, Error, string[], unknown>>;
  displayName: string;
}) {
  return (
    <>
      {displayName && (
        <h2 className="text-lg font-semibold mb-4">{displayName} Sessions</h2>
      )}
      <DataTable
        columns={sessionColumns}
        data={sessionData}
        emptyMessage="No sessions found for this agent."
      />

      {sessionData.some((s) => s.status === 'running') && (
        <div className="mt-4 p-3 bg-neutral-800 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-400">
              {sessionData.filter((s) => s.status === 'running').length} running session(s)
            </span>
            <Button
              variant="destructive"
              size="sm"
              disabled={terminate.isPending}
              onClick={() => {
                const runningIds = sessionData
                  .filter((s) => s.status === 'running')
                  .map((s) => s.id);
                if (runningIds.length > 0) {
                  terminate.mutate(runningIds);
                }
              }}
            >
              {terminate.isPending ? 'Terminating...' : `Terminate All Running`}
            </Button>
          </div>
        </div>
      )}

      {sessionData.some((s) => s.status === 'running') && (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-sm text-neutral-400">Stream session:</span>
          {sessionData
            .filter((s) => s.status === 'running')
            .map((s) => (
              <Button
                key={s.id}
                variant={streamSessionId === s.id ? 'default' : 'outline'}
                size="sm"
                onClick={() =>
                  setStreamSessionId(streamSessionId === s.id ? null : s.id)
                }
              >
                {s.id.slice(0, 8)}...
              </Button>
            ))}
        </div>
      )}

      <SessionMessageInput
        sessionId={streamSessionId}
        sessionStatus={sessionData.find((s) => s.id === streamSessionId)?.status}
      />
      <LiveStreamPanel sessionId={streamSessionId} />
    </>
  );
}
